import { useState, useEffect, useCallback } from "react";
import api from "../../api/client";
import TeamNumberSelect from "./TeamNumberSelect";
import PersonnelSelect from "./PersonnelSelect";

/**
 * TeamPersonnelGroup — Team Number dropdown + Personnel 1 / Personnel 2 dropdowns.
 *
 * Auto-fills personnel from daily composition when a team is selected.
 * Fetches daily-occupied personnel to disable already-assigned people.
 *
 * Props:
 *   teamMasterId       — selected team_master_id (number | null)
 *   personnel1Id       — selected personnel 1 user id (number | null)
 *   personnel2Id       — selected personnel 2 user id (number | null)
 *   onTeamChange       — (teamMasterId, teamData) => void
 *   onPersonnel1Change — (userId, userData) => void
 *   onPersonnel2Change — (userId, userData) => void
 *   companyId          — company scope
 *   assignmentDate     — "YYYY-MM-DD" for composition + occupancy lookup
 *   disabled           — disable all controls
 *   disabledTeam       — disable only team dropdown
 *   disabledPersonnel  — disable only personnel dropdowns
 *   className          — wrapper CSS
 */
export default function TeamPersonnelGroup({
  teamMasterId,
  personnel1Id,
  personnel2Id,
  onTeamChange,
  onPersonnel1Change,
  onPersonnel2Change,
  companyId,
  assignmentDate,
  disabled = false,
  disabledTeam = false,
  disabledPersonnel = false,
  className = "",
}) {
  const [occupiedMap, setOccupiedMap] = useState({});        // { userId: teamNumber }
  const [personnelList, setPersonnelList] = useState(null);  // shared list to avoid duplicate calls

  // Fetch personnel list once
  useEffect(() => {
    const params = {};
    if (companyId) params.company_id = companyId;
    api
      .get("/team-masters/personnel", { params })
      .then(({ data }) => setPersonnelList(data))
      .catch(() => setPersonnelList([]));
  }, [companyId]);

  // Fetch daily-occupied personnel
  useEffect(() => {
    if (!assignmentDate) { setOccupiedMap({}); return; }
    const params = { assignment_date: assignmentDate };
    if (companyId) params.company_id = companyId;
    if (teamMasterId) params.exclude_team_master_id = teamMasterId;
    api
      .get("/team-masters/daily-occupied", { params })
      .then(({ data }) => {
        const map = {};
        (data || []).forEach((item) => { map[item.user_id] = item.team_number; });
        setOccupiedMap(map);
      })
      .catch(() => setOccupiedMap({}));
  }, [companyId, assignmentDate, teamMasterId]);

  // When team changes, auto-fill personnel from composition
  const handleTeamChange = useCallback(
    (teamId, teamData) => {
      onTeamChange(teamId, teamData);
      if (!teamId) {
        onPersonnel1Change(null, null);
        onPersonnel2Change(null, null);
        return;
      }
      // Fetch effective composition for this team + date
      if (assignmentDate) {
        api
          .get("/team-masters/composition", {
            params: { team_master_id: teamId, assignment_date: assignmentDate },
          })
          .then(({ data: comp }) => {
            if (comp) {
              onPersonnel1Change(comp.personnel_1_id || null, null);
              onPersonnel2Change(comp.personnel_2_id || null, null);
            }
          })
          .catch(() => {});
      }
    },
    [assignmentDate, onTeamChange, onPersonnel1Change, onPersonnel2Change]
  );

  const occupiedIds = new Set(Object.keys(occupiedMap).map(Number));

  // Cross-disable: Personnel 1 dropdown disables whoever is selected as Personnel 2, and vice versa
  const disabledForP1 = new Set(occupiedIds);
  if (personnel2Id) disabledForP1.add(personnel2Id);

  const disabledForP2 = new Set(occupiedIds);
  if (personnel1Id) disabledForP2.add(personnel1Id);

  const disabledReasonP1 = (userId) =>
    occupiedMap[userId] ? `Assigned to ${occupiedMap[userId]}` : "Already selected as Personnel 2";
  const disabledReasonP2 = (userId) =>
    occupiedMap[userId] ? `Assigned to ${occupiedMap[userId]}` : "Already selected as Personnel 1";

  return (
    <div className={className}>
      {/* Team Number */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Team Number <span className="text-red-500">*</span>
        </label>
        <TeamNumberSelect
          value={teamMasterId}
          onChange={handleTeamChange}
          companyId={companyId}
          assignmentDate={assignmentDate}
          disabled={disabled || disabledTeam}
        />
      </div>

      {/* Personnel 1 */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Personnel 1 <span className="text-red-500">*</span>
        </label>
        <PersonnelSelect
          value={personnel1Id}
          onChange={onPersonnel1Change}
          companyId={companyId}
          disabled={disabled || disabledPersonnel}
          label="— Select Personnel 1 —"
          personnelList={personnelList}
          disabledIds={disabledForP1}
          disabledReason={disabledReasonP1}
        />
      </div>

      {/* Personnel 2 */}
      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1">
          Personnel 2
        </label>
        <PersonnelSelect
          value={personnel2Id}
          onChange={onPersonnel2Change}
          companyId={companyId}
          disabled={disabled || disabledPersonnel}
          label="— Select Personnel 2 —"
          allowNone
          personnelList={personnelList}
          disabledIds={disabledForP2}
          disabledReason={disabledReasonP2}
        />
      </div>
    </div>
  );
}
